import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

/**
 * Reusable, stylized confirmation dialog — a drop-in replacement for window.confirm().
 *
 * Controlled via `open`. While awaiting `onConfirm` it shows an in-flight spinner and
 * disables both buttons. On success it calls `onClose`; if `onConfirm` throws, the dialog
 * stays open (the caller is expected to surface the error, e.g. a snackbar, and rethrow)
 * so the action can be retried or cancelled.
 *
 * Props:
 *   open         - whether the dialog is shown
 *   title        - heading text
 *   message      - body: a string (wrapped in Typography) or any React node
 *   confirmLabel - confirm button text (default "Confirm")
 *   cancelLabel  - cancel button text (default "Cancel")
 *   confirmColor - MUI color for the confirm button (default "primary"; use "error" for destructive)
 *   confirmIcon  - optional icon node for the confirm button
 *   icon         - optional icon node shown next to the title
 *   onConfirm    - async action to run on confirm (may throw to keep the dialog open)
 *   onClose      - close handler (clears the controlling state)
 */
export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmColor = 'primary',
  confirmIcon = null,
  icon = null,
  onConfirm,
  onClose,
}) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) {
      onClose?.();
      return;
    }
    setBusy(true);
    try {
      await onConfirm();
      onClose?.();
    } catch (_) {
      // Caller surfaces the error and rethrows; keep the dialog open for retry/cancel.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={() => !busy && onClose?.()} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        {typeof message === 'string' ? <Typography variant="body2">{message}</Typography> : message}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : confirmIcon}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
